'use client';

import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';

export type CatalogItemOption = {
  id: number;
  upc: string;
  name?: string | null;
};

const SEARCH_DEBOUNCE_MS = 300;
const MAX_OPTIONS = 20;

type Props = {
  value: CatalogItemOption | null;
  onChange: (item: CatalogItemOption | null) => void;
  searchCatalog: (search: string) => Promise<CatalogItemOption[]>;
  label?: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  sx?: object;
  disabled?: boolean;
  excludeItemId?: number;
  /** Prefill options (e.g. OCR search candidates) before the user types. */
  initialOptions?: CatalogItemOption[];
  /** Seed the input when value is empty (e.g. OCR description / search query). */
  initialInputValue?: string;
};

function optionLabel(item: CatalogItemOption): string {
  const name = item.name?.trim();
  return name ? `${item.upc} — ${name}` : item.upc;
}

const CatalogItemAutocomplete: React.FC<Props> = ({
  value,
  onChange,
  searchCatalog,
  label = 'Catalog item',
  placeholder = 'Search UPC or name…',
  size = 'small',
  sx,
  disabled,
  excludeItemId,
  initialOptions,
  initialInputValue,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<CatalogItemOption[]>(initialOptions ?? []);
  const [loading, setLoading] = useState(false);
  const seededRef = useRef(false);

  useEffect(() => {
    if (value && !inputValue) {
      setInputValue(optionLabel(value));
    }
  }, [value, inputValue]);

  useEffect(() => {
    if (seededRef.current) return;
    if (value) return;
    if (initialInputValue?.trim()) {
      setInputValue(initialInputValue.trim());
      seededRef.current = true;
    } else if (initialOptions && initialOptions.length > 0) {
      seededRef.current = true;
    }
  }, [initialInputValue, initialOptions, value]);

  useEffect(() => {
    if (initialOptions && initialOptions.length > 0 && !inputValue.trim()) {
      const filtered =
        excludeItemId != null
          ? initialOptions.filter((r) => r.id !== excludeItemId).slice(0, MAX_OPTIONS)
          : initialOptions.slice(0, MAX_OPTIONS);
      setOptions(filtered);
    }
  }, [initialOptions, excludeItemId, inputValue]);

  useEffect(() => {
    const term = inputValue.trim();
    if (term.length < 1) {
      if (initialOptions && initialOptions.length > 0) {
        const filtered =
          excludeItemId != null
            ? initialOptions.filter((r) => r.id !== excludeItemId).slice(0, MAX_OPTIONS)
            : initialOptions.slice(0, MAX_OPTIONS);
        setOptions(filtered);
      } else {
        setOptions([]);
      }
      setLoading(false);
      return;
    }
    let active = true;
    const timer = window.setTimeout(() => {
      setLoading(true);
      void searchCatalog(term)
        .then((rows) => {
          if (!active) return;
          const filtered =
            excludeItemId != null
              ? rows.filter((r) => r.id !== excludeItemId).slice(0, MAX_OPTIONS)
              : rows.slice(0, MAX_OPTIONS);
          setOptions(filtered);
        })
        .catch(() => {
          if (active) setOptions([]);
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [inputValue, searchCatalog, excludeItemId, initialOptions]);

  return (
    <Autocomplete<CatalogItemOption>
      options={options}
      loading={loading}
      filterOptions={(x) => x}
      disabled={disabled}
      size={size}
      sx={{ minWidth: 280, ...sx }}
      value={value}
      onChange={(_, v) => onChange(v)}
      inputValue={inputValue}
      onInputChange={(_, v, reason) => {
        if (reason === 'reset' && value) {
          setInputValue(optionLabel(value));
          return;
        }
        setInputValue(v);
      }}
      getOptionLabel={optionLabel}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      noOptionsText={inputValue.trim() ? 'No matching catalog items' : 'Type to search catalog'}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Box>
            <Typography variant="body2">{option.upc}</Typography>
            {option.name?.trim() ? (
              <Typography variant="caption" color="text.secondary">
                {option.name}
              </Typography>
            ) : null}
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder={placeholder} size={size} />
      )}
    />
  );
};

export default CatalogItemAutocomplete;
